import { useRef, useState } from "react";
import { useTranslations } from "use-intl";
import {
  AirplaneTakeoffIcon,
  AirplaneLandingIcon,
  CircleNotchIcon,
  MapPinIcon,
} from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import type { FormikProps } from "formik";
import { Divider, TextField } from "@mui/material";
import type {
  FlightFormLocation,
  TFlightFormValues,
} from "@/app/lib/types/types";
import { AirportType } from "@/app/lib/types/enums";
import { initialFlightsValues } from "@/app/lib/initialFlightsValues";
import { useAirportSearch } from "@/app/lib/hooks/useAirportSearch";
import { usePreviousAirportSearches } from "@/app/lib/hooks/usePreviousSearches";
import { SearchResultList, SearchResultButton } from "../SearchResultList";
import SwapValuesButton from "../SwapValuesButton";
import { LastSearchedAirportList } from "../LastSearchedAirportList";
import { CitiesRecommendations } from "../CitiesRecommendations";
import {
  topOriginsQueryOptions,
  topDestinationsQueryOptions,
} from "@/app/lib/hooks/useTopLocations";
import { TopLocationList } from "../TopLocationList";
import { rudderAnalytics, SearchTrackingEvents } from "@/app/lib/rudderstack";
import { ResetButton } from "../ResetButton";

type Props = {
  onDrawerClose: () => void;
  formProps: FormikProps<TFlightFormValues>;
  initialFocusOn: AirportType;
  analyticsSource: "main" | "sticky";
};

function AirportSearch({
  onDrawerClose,
  formProps,
  initialFocusOn,
  analyticsSource,
}: Props) {
  const t = useTranslations();

  const [departureInput, setDepartureInput] = useState(
    formProps.values.from?.name || "",
  );
  const [isDepartureDirty, setIsDepartureDirty] = useState(
    departureInput !== formProps.values.from?.name,
  );
  const [arrivalInput, setArrivalInput] = useState(
    formProps.values.to?.name || "",
  );
  const [isArrivalDirty, setIsArrivalDirty] = useState(
    arrivalInput !== formProps.values.to?.name,
  );
  const [focus, setFocus] = useState(initialFocusOn);

  const { previousSearches, addPreviousSearch } = usePreviousAirportSearches();
  const { data: topOrigins } = useQuery(topOriginsQueryOptions());
  const { data: topDestinations } = useQuery(topDestinationsQueryOptions());
  const filteredPreviousSearches = previousSearches.filter((location) => {
    return (
      (focus === AirportType.FROM && formProps.values.to?.id !== location.id) ||
      (focus === AirportType.TO && formProps.values.from?.id !== location.id)
    );
  });
  const hasCityRecommendations = focus === AirportType.FROM;
  const hasPreviousSearches = filteredPreviousSearches.length > 0;
  const topLocations =
    focus === AirportType.FROM ? topOrigins : topDestinations;

  // prevent search if input is not dirty to avoid replacing
  // recommendations and recent searches with search results
  const queries = {
    [AirportType.FROM]: useAirportSearch(
      isDepartureDirty ? departureInput || "" : "",
    ),
    [AirportType.TO]: useAirportSearch(
      isArrivalDirty ? arrivalInput || "" : "",
    ),
  };

  const { data, isFetching, isSuccess } = queries[focus];

  const fromInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);

  const setCurrentInputValue = (value: string) => {
    const newValue = value.slice(0, 30);

    if (focus === AirportType.FROM) {
      setDepartureInput(newValue);
    } else {
      setArrivalInput(newValue);
    }
  };

  const handleClick = async (value: FlightFormLocation) => {
    setCurrentInputValue(value.name);

    if (focus === AirportType.FROM) {
      await formProps.setFieldValue("from", value);
      if (formProps.values.to?.name) {
        onDrawerClose();
      } else {
        toInputRef.current?.focus();
      }
    } else {
      await formProps.setFieldValue("to", value);
      if (formProps.values.from?.name) {
        onDrawerClose();
      } else {
        fromInputRef.current?.focus();
      }
    }
  };

  const resetArrival = async () => {
    await formProps.setFieldValue("to", initialFlightsValues.to);
    setArrivalInput("");
    toInputRef.current?.focus();
  };

  const resetDeparture = async () => {
    await formProps.setFieldValue("from", initialFlightsValues.from);
    setDepartureInput("");
    fromInputRef.current?.focus();
  };

  const changeAirportValues = async () => {
    await formProps.setFieldValue("from", formProps.values.to);
    await formProps.setFieldValue("to", formProps.values.from);
    setDepartureInput(formProps.values.to?.name || "");
    setArrivalInput(formProps.values.from?.name || "");
    if (focus === AirportType.FROM) {
      fromInputRef.current?.focus();
    } else {
      toInputRef.current?.focus();
    }
  };

  return (
    <div className="flex w-full shrink-0 flex-col">
      <div className="relative flex w-full shrink-0 flex-col gap-4">
        <TextField
          name="from"
          fullWidth
          autoComplete="off"
          autoFocus={focus === AirportType.FROM}
          focused={focus === AirportType.FROM}
          sx={{
            input: {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
          value={departureInput}
          onChange={(e) => {
            setCurrentInputValue(e.currentTarget.value);
            setIsDepartureDirty(true);
          }}
          onFocus={(e) => {
            // if airport search was opened and focused on the same input, select all text to make it easier to replace
            if (focus === AirportType.FROM) {
              e.currentTarget.select();
            }
            setFocus(AirportType.FROM);
          }}
          onBlur={() => {
            setDepartureInput(formProps.values.from?.name || "");
          }}
          slotProps={{
            input: {
              inputRef: fromInputRef,
              endAdornment: formProps.values.from?.name && departureInput && (
                <ResetButton
                  onClick={resetDeparture}
                  aria-label={t("flights.search.resetDeparture")}
                />
              ),
            },
          }}
          label={
            <div className="flex items-center gap-2">
              <AirplaneTakeoffIcon size={24} />
              <span>{t("flights.search.from")}</span>
            </div>
          }
        />
        <SwapValuesButton
          onClick={changeAirportValues}
          className="absolute end-5 top-11 z-10"
        />
        <TextField
          name="to"
          fullWidth
          autoComplete="off"
          autoFocus={focus === AirportType.TO}
          focused={focus === AirportType.TO}
          sx={{
            input: {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
          value={arrivalInput}
          onChange={(e) => {
            setCurrentInputValue(e.currentTarget.value);
            setIsArrivalDirty(true);
          }}
          onFocus={(e) => {
            // if airport search was opened and focused on the same input, select all text to make it easier to replace
            if (focus === AirportType.TO) {
              e.currentTarget.select();
            }
            setFocus(AirportType.TO);
          }}
          onBlur={() => {
            setArrivalInput(formProps.values.to?.name || "");
          }}
          slotProps={{
            input: {
              inputRef: toInputRef,
              endAdornment: formProps.values.to?.name && arrivalInput && (
                <ResetButton
                  onClick={resetArrival}
                  aria-label={t("flights.search.resetArrival")}
                />
              ),
            },
          }}
          label={
            <div className="flex items-center gap-2">
              <AirplaneLandingIcon size={24} />
              <span>{t("flights.search.to")}</span>
            </div>
          }
        />
      </div>
      {isFetching ? (
        <div className="mt-6 flex justify-center text-sm text-text-placeholder">
          <CircleNotchIcon className="animate-spin" size={30} />
        </div>
      ) : isSuccess ? (
        data?.length > 0 ? (
          <SearchResultList
            data={data}
            getItemKey={(city) => city.id}
            renderItem={(city) => {
              const shouldBeHidden =
                (focus === AirportType.FROM &&
                  formProps.values.to?.id === city.id) ||
                (focus === AirportType.TO &&
                  formProps.values.from?.id === city.id);

              const selectLocation = (location: FlightFormLocation) => {
                const trackerData = {
                  location_id: location.id,
                  location_name: location.name,
                  location_type: location.type,
                };

                if (focus === AirportType.FROM) {
                  rudderAnalytics.track(
                    SearchTrackingEvents.USER_SELECTED_SEARCH_ORIGIN,
                    { ...trackerData, source: analyticsSource },
                  );
                } else {
                  rudderAnalytics.track(
                    SearchTrackingEvents.USER_SELECTED_SEARCH_DESTINATION,
                    { ...trackerData, source: analyticsSource },
                  );
                }

                addPreviousSearch(location);
                handleClick(location);
              };

              return shouldBeHidden ? null : (
                <>
                  <SearchResultButton
                    icon={MapPinIcon}
                    primaryText={city.name}
                    secondaryText={city.countryName}
                    onClick={() => {
                      selectLocation(city);
                    }}
                  />

                  <ul className="block ps-4">
                    {city.airports.map((airport) => (
                      <SearchResultButton
                        key={airport.id}
                        icon={AirplaneLandingIcon}
                        primaryText={airport.name}
                        code={airport.code}
                        onClick={() => {
                          selectLocation(airport);
                        }}
                      />
                    ))}
                  </ul>
                </>
              );
            }}
          />
        ) : (
          <div className="mt-6 text-center">
            <div className="mb-1 text-base font-medium">
              {t("flights.search.nothingFound")}
            </div>
            <div className="text-sm text-text-placeholder">
              {t("flights.search.checkInput")}
            </div>
          </div>
        )
      ) : (
        <>
          {hasCityRecommendations ? (
            <CitiesRecommendations
              onRecommendationClick={(city) => {
                rudderAnalytics.track(
                  SearchTrackingEvents.USER_SELECTED_ORIGIN_RECOMMENDATION,
                  {
                    city_id: city.id,
                    city_name: city.name,
                    source: analyticsSource,
                  },
                );
                handleClick(city);
              }}
              shouldShow={(city) => formProps.values.to?.id !== city.id}
            />
          ) : null}

          {hasPreviousSearches ? (
            <LastSearchedAirportList
              previousSearches={filteredPreviousSearches}
              onClick={(location) => {
                const trackerData = {
                  location_id: location.id,
                  location_name: location.name,
                  location_type: location.type,
                };

                if (focus === AirportType.FROM) {
                  rudderAnalytics.track(
                    SearchTrackingEvents.USER_SELECTED_RECENT_ORIGIN,
                    { ...trackerData, source: analyticsSource },
                  );
                } else {
                  rudderAnalytics.track(
                    SearchTrackingEvents.USER_SELECTED_RECENT_DESTINATION,
                    { ...trackerData, source: analyticsSource },
                  );
                }

                handleClick(location);
              }}
            />
          ) : null}

          {topLocations?.length ? (
            <>
              {hasPreviousSearches || hasCityRecommendations ? (
                <Divider
                  sx={{
                    mt: "calc(var(--spacing) * 4)",
                    borderColor: "var(--color-stroke-neutral-soft)",
                  }}
                />
              ) : null}
              <TopLocationList
                topLocations={topLocations}
                onClick={(location) => {
                  const trackerData = {
                    location_id: location.id,
                    location_name: location.name,
                    location_type: location.type,
                  };

                  if (focus === AirportType.FROM) {
                    rudderAnalytics.track(
                      SearchTrackingEvents.USER_SELECTED_TOP_ORIGIN,
                      { ...trackerData, source: analyticsSource },
                    );
                  } else {
                    rudderAnalytics.track(
                      SearchTrackingEvents.USER_SELECTED_TOP_DESTINATION,
                      { ...trackerData, source: analyticsSource },
                    );
                  }

                  handleClick(location);
                }}
              />
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

export default AirportSearch;
